import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { HttpService } from './http.service';
import {
  QRTemplatesListResponse,
  DEFAULT_QR_TEMPLATES_CURRENT_PAGE,
  DEFAULT_QR_TEMPLATES_PER_PAGE,
} from '../../store/entities/models/qr-template.model';
import {
  QRTemplateDetail,
  CreateQRTemplatePayload,
  UpdateQRTemplatePayload,
} from '../../store/entities/models/qr-template-detail.model';
import { transformSnakeToCamel } from '../../store/utils/transformers';
import { ApiTableFilter } from '../../shared/models/table-filter.model';

export interface QRTemplatesListParams {
  page?: number;
  per_page?: number;
  search?: string;
  sort?: string;
  sort_direction?: string;
  filters?: ApiTableFilter[];
  filter_link?: string;
}

interface ApiResponse<T> {
  data: T;
  message: string;
  success: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class QRTemplatesService {
  private http = inject(HttpService);

  getQRTemplatesList(params: QRTemplatesListParams = {}): Observable<QRTemplatesListResponse> {
    return this.http
      .get<Record<string, unknown>>('/qr/templates', {
        page: params.page ?? DEFAULT_QR_TEMPLATES_CURRENT_PAGE,
        per_page: params.per_page ?? DEFAULT_QR_TEMPLATES_PER_PAGE,
        sort: params.sort ?? 'created_at',
        sort_direction: params.sort_direction ?? 'desc',
        search: params.search ?? '',
        filters: params.filters,
        filter_link: params.filter_link,
      })
      .pipe(map((response) => transformSnakeToCamel(response) as QRTemplatesListResponse));
  }

  getQRTemplateById(uuid: string): Observable<QRTemplateDetail> {
    return this.http
      .get<Record<string, unknown>>(`/qr/templates/${uuid}`)
      .pipe(
        map((response) => {
          const transformed = transformSnakeToCamel(response) as ApiResponse<QRTemplateDetail>;

          return transformed.data;
        })
      );
  }

  createQRTemplate(payload: CreateQRTemplatePayload): Observable<QRTemplateDetail> {
    const formData = this.buildFormData(payload);

    return this.http
      .post<Record<string, unknown>>('/qr/templates', formData)
      .pipe(
        map((response) => {
          const transformed = transformSnakeToCamel(response) as ApiResponse<QRTemplateDetail>;

          return transformed.data;
        })
      );
  }

  updateQRTemplate(uuid: string, payload: UpdateQRTemplatePayload): Observable<QRTemplateDetail> {
    const formData = this.buildFormData(payload);

    return this.http
      .post<Record<string, unknown>>(`/qr/templates/${uuid}/`, formData)
      .pipe(
        map((response) => {
          const transformed = transformSnakeToCamel(response) as ApiResponse<QRTemplateDetail>;

          return transformed.data;
        })
      );
  }

  deleteQRTemplate(uuid: string): Observable<void> {
    return this.http.delete<void>(`/qr/templates/${uuid}`);
  }

  private buildFormData(payload: CreateQRTemplatePayload | UpdateQRTemplatePayload): FormData {
    const formData = new FormData();

    // Transform each key individually to snake_case while preserving the original values
    Object.entries(payload).forEach(([camelKey, value]) => {
      const snakeKey = this.camelToSnake(camelKey);

      if (value !== undefined && value !== null) {
        if (value instanceof File) {
          formData.append(snakeKey, value);
        } else if (Array.isArray(value)) {
          value.forEach((item, index) => {
            formData.append(`${snakeKey}[${index}]`, String(item));
          });
        } else if (typeof value === 'object') {
          formData.append(snakeKey, JSON.stringify(value));
        } else {
          formData.append(snakeKey, String(value));
        }
      }
    });

    return formData;
  }

  private camelToSnake(str: string): string {
    return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
  }
}
